import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { SearchIcon } from "lucide-react";
import { useTranslation } from "react-i18next";
import { useAtom } from "jotai";

import { sidebarCollapsedAtom } from "@/store";
import { useAuth } from "@/hooks/useAuth";
import { useApplications } from "@/hooks/useApplication";
import { useDebounce } from "@/hooks/useDebounce";
import { ReviewStatus } from "@/types/application";
import { ApprovalTable } from "@/components/table/ApprovalTable";
import { ReviewStatusTag } from "@/components/application-status-tag";
import { BackIcon } from "@/components/icons";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuRadioGroup,
  DropdownMenuRadioItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import Menu, { MenuItem } from "@/components/ui/menu";
import { cn } from "@/utils/cn";
import { ChevronDown } from "lucide-react";

type ApprovalListTab = "pending" | "history";
type ApprovalSearchMode = "applicationName" | "serialNumber" | "summary";

export default function ApprovalListPage() {
  const { t } = useTranslation();
  const { user } = useAuth();
  const [, setSidebarCollapsed] = useAtom(sidebarCollapsedAtom);

  const [activeTab, setActiveTab] = useState<ApprovalListTab>("pending");
  const [searchMode, setSearchMode] =
    useState<ApprovalSearchMode>("applicationName");
  const [searchQuery, setSearchQuery] = useState("");
  const debouncedSearchQuery = useDebounce(searchQuery, 700);

  const [reviewStatusFilter, setReviewStatusFilter] =
    useState<ReviewStatus | null>(null);
  const [approvalDateSorter, setApprovalDateSorter] = useState<"asc" | "desc">(
    "desc",
  );
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);

  useEffect(() => {
    setSidebarCollapsed(true);
  }, [setSidebarCollapsed]);

  useEffect(() => {
    setPage(1);
  }, [
    activeTab,
    searchMode,
    debouncedSearchQuery,
    reviewStatusFilter,
    approvalDateSorter,
  ]);

  const derivedReviewStatusFilter =
    activeTab === "pending"
      ? ReviewStatus.Pending
      : reviewStatusFilter === null
        ? undefined
        : reviewStatusFilter;
  const derivedApprovalStatusFilter = useMemo(() => {
    if (activeTab === "history" && !derivedReviewStatusFilter) {
      return ["APPROVED", "REJECTED", "CANCELLED"] as const;
    }
    if (!derivedReviewStatusFilter) return undefined;
    switch (derivedReviewStatusFilter) {
      case ReviewStatus.Approved:
        return ["APPROVED"] as const;
      case ReviewStatus.Canceled:
        return ["CANCELLED"] as const;
      case ReviewStatus.NotStarted:
        return ["WAITING"] as const;
      case ReviewStatus.Pending:
        return ["PENDING"] as const;
      case ReviewStatus.Rejected:
        return ["REJECTED"] as const;
      default:
        return undefined;
    }
  }, [activeTab, derivedReviewStatusFilter]);

  const searchModeOptions = useMemo(
    () => [
      {
        label: t("dashboard.search_by_application_name"),
        value: "applicationName" as const,
      },
      {
        label: t("dashboard.search_by_serial_number"),
        value: "serialNumber" as const,
      },
      {
        label: t("dashboard.search_by_summary", { defaultValue: "Summary" }),
        value: "summary" as const,
      },
    ],
    [t],
  );

  const handleSearchModeChange = (value: string) => {
    setSearchMode(value as ApprovalSearchMode);
    setSearchQuery("");
    setPage(1);
  };

  const searchModeLabel =
    searchMode === "serialNumber"
      ? t("dashboard.search_by_serial_number")
      : searchMode === "summary"
        ? t("dashboard.search_by_summary", { defaultValue: "Summary" })
      : t("dashboard.search_by_application_name");

  const searchPlaceholder =
    searchMode === "serialNumber"
      ? t("dashboard.search_serial_number")
      : searchMode === "summary"
        ? t("dashboard.search_summary", {
            defaultValue: "Search summary...",
          })
      : t("dashboard.search_application_name");

  const { applications, isLoading } = useApplications({
    type: "approval",
    page,
    pageSize,
    filter: {
      assigneeId: user?.id,
      approvalStatus: derivedApprovalStatusFilter
        ? [...derivedApprovalStatusFilter]
        : undefined,
      reviewStatus: derivedReviewStatusFilter,
      formName:
        searchMode === "applicationName"
          ? debouncedSearchQuery || undefined
          : undefined,
      serialNumber:
        searchMode === "serialNumber"
          ? debouncedSearchQuery || undefined
          : undefined,
      summary:
        searchMode === "summary" ? debouncedSearchQuery || undefined : undefined,
    },
    sorter: {
      submittedAt: approvalDateSorter,
      sortBy: "applied_at",
    },
  });

  const tablePaginationProps = useMemo(() => {
    if (!applications) return undefined;
    const totalItems = applications.total ?? applications.items.length;

    return {
      currentPage: applications.page ?? page,
      pageSize: applications.limit ?? pageSize,
      totalItems,
      onPageChange: setPage,
      pageSizeOptions: [10, 25, 50, 100],
      onPageSizeChange: (size: number) => {
        setPageSize(size);
        setPage(1);
      },
    };
  }, [applications, page, pageSize]);

  const historyStatusMenuItems = useMemo<MenuItem[]>(
    () => [
      {
        label: t("dashboard.all_status"),
        onClick: () => {
          setReviewStatusFilter(null);
          setPage(1);
        },
      },
      {
        label: <ReviewStatusTag status={ReviewStatus.Rejected} />,
        onClick: () => {
          setReviewStatusFilter(ReviewStatus.Rejected);
          setPage(1);
        },
      },
      {
        label: <ReviewStatusTag status={ReviewStatus.Approved} />,
        onClick: () => {
          setReviewStatusFilter(ReviewStatus.Approved);
          setPage(1);
        },
      },
      {
        label: <ReviewStatusTag status={ReviewStatus.Canceled} />,
        onClick: () => {
          setReviewStatusFilter(ReviewStatus.Canceled);
          setPage(1);
        },
      },
    ],
    [t],
  );

  return (
    <div className="max-h-full overflow-y-auto h-full bg-gray-2 max-w-dvw">
      <div className="flex flex-col items-center justify-center">
        <div className="flex flex-row gap-3 sm:gap-5 text-dark text-lg font-medium items-center py-2 px-4 sm:px-5 bg-white sticky top-0 border-b border-b-stroke z-10 max-w-full w-full">
          <Link to="/dashboard">
            <Button variant="tertiary" className="h-11 w-11 shrink-0 p-2">
              <BackIcon className="text-primary-text" />
            </Button>
          </Link>
          <span className="min-w-0 truncate">
            {t("dashboard.approval_review_title")}
          </span>
        </div>

        <div className="lg:max-w-7xl md:max-w-4xl w-full min-w-0 flex flex-col gap-5 py-4 px-4 sm:py-5 sm:px-5">
          <div className="-mx-4 flex gap-2.5 overflow-x-auto px-4 pb-1 sm:mx-0 sm:px-0">
            <button
              type="button"
              onClick={() => setActiveTab("pending")}
              className={cn(
                "h-11 shrink-0 whitespace-nowrap px-4 py-2 rounded-lg text-sm font-medium sm:px-5 sm:text-base",
                activeTab === "pending"
                  ? "bg-giant-blue text-white"
                  : "bg-white border border-stroke text-dark",
              )}
            >
              {t("dashboard.pending_approvals")}
            </button>
            <button
              type="button"
              onClick={() => setActiveTab("history")}
              className={cn(
                "h-11 shrink-0 whitespace-nowrap px-4 py-2 rounded-lg text-sm font-medium sm:px-5 sm:text-base",
                activeTab === "history"
                  ? "bg-giant-blue text-white"
                  : "bg-white border border-stroke text-dark",
              )}
            >
              {t("dashboard.approval_history")}
            </button>
          </div>

          <div className="flex w-full flex-col items-stretch gap-2.5 md:flex-row md:justify-between">
            <div className="min-w-0 w-full md:max-w-[620px] bg-white rounded-lg border border-stroke flex flex-col justify-start sm:flex-row sm:items-center">
              <div className="w-full border-b border-stroke px-4 py-2.5 sm:w-auto sm:border-b-0 sm:border-r sm:px-5 flex justify-center items-center gap-2.5">
                <DropdownMenu>
                  <DropdownMenuTrigger asChild>
                    <button
                      type="button"
                      className="inline-flex w-full min-w-0 items-center justify-between gap-2.5 sm:w-[197px]"
                    >
                      <span className="min-w-0 truncate text-left text-base text-dark">
                        {searchModeLabel}
                      </span>
                      <ChevronDown className="h-4 w-4 shrink-0 text-secondary-text" />
                    </button>
                  </DropdownMenuTrigger>
                  <DropdownMenuContent
                    align="start"
                    className="min-w-[220px] w-[var(--radix-dropdown-menu-trigger-width)]"
                  >
                    <DropdownMenuRadioGroup
                      value={searchMode}
                      onValueChange={handleSearchModeChange}
                    >
                      {searchModeOptions.map((option) => (
                        <DropdownMenuRadioItem
                          key={option.value}
                          value={option.value}
                          className="h-10 cursor-pointer text-base text-dark"
                        >
                          {option.label}
                        </DropdownMenuRadioItem>
                      ))}
                    </DropdownMenuRadioGroup>
                  </DropdownMenuContent>
                </DropdownMenu>
              </div>
              <div className="relative w-full min-w-0 flex-1">
                <Input
                  type="text"
                  placeholder={searchPlaceholder}
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  className="rounded-none rounded-b-lg border-0 sm:rounded-b-none sm:rounded-r-lg"
                  icon={<SearchIcon className="w-5 h-5 text-secondary-text" />}
                  hasClearIcon
                />
              </div>
            </div>
            {activeTab === "history" && (
              <div className="w-full md:w-[220px]">
                <Menu
                  items={historyStatusMenuItems}
                  trigger={
                    <button
                      type="button"
                      className="h-12 w-full rounded-[6px] border border-stroke bg-white px-4 py-2.5 flex items-center justify-between text-left"
                    >
                      <span className="inline-flex min-w-0 items-center">
                        {reviewStatusFilter ? (
                          <ReviewStatusTag status={reviewStatusFilter} />
                        ) : (
                          <span className="truncate text-base text-dark">
                            {t("dashboard.all_status")}
                          </span>
                        )}
                      </span>
                      <ChevronDown className="h-4 w-4 shrink-0 text-secondary-text" />
                    </button>
                  }
                />
              </div>
            )}
          </div>

          <ApprovalTable
            applications={applications?.items ?? []}
            loading={isLoading}
            empty={
              <div className="flex flex-col w-full h-full items-center align-middle justify-center min-h-100">
                <div className="text-primary-text text-lg font-semibold">
                  {activeTab === "pending"
                    ? t("dashboard.no_pending_approvals")
                    : t("dashboard.no_approval_history")}
                </div>
              </div>
            }
            showActions={activeTab === "pending"}
            filter={undefined}
            sorter={{
              value: approvalDateSorter,
              onSorter: (value) => {
                setApprovalDateSorter(value);
                setPage(1);
              },
            }}
            pagination={tablePaginationProps}
          />
        </div>
      </div>
    </div>
  );
}
